import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Steps,
  Card,
  Radio,
  Form,
  Input,
  Select,
  Button,
  Space,
  Alert,
  Spin,
  message,
  Typography,
  Tag,
  Empty,
  Table,
} from 'antd';
import { useAuth } from '../../auth/AuthContext';
import { ApiClient } from '../../services/apiClient';
import {
  getAssets,
  createAsset,
  getUsers,
  createUser,
  Asset,
  User,
  Organization,
  getOrganizations,
  CreateUserData,
  getSims,
  assignSimToUser,
  UnifiedSim,
} from '../../services/adminService';
import LookupSelect from '../../components/LookupSelect';
import UserForm, { UserFormValues } from '../../components/UserForm';
import PageHeader from '../../components/PageHeader';
import PageHelp from '../../components/PageHelp';

const { Text } = Typography;

// Mirrors backend ALLOWED_CHILD_ROLES (lambda/admin/lib/auth.js). Drives which
// existing users are pickable on step 2 — the caller can only assign someone
// whose role they could have created themselves.
const ALLOWED_CHILD_ROLES: Record<string, string[]> = {
  super_admin: [
    'super_admin',
    'reseller_admin',
    'reseller_support',
    'customer_admin',
    'asset_manager',
    'department_head',
    'crew',
  ],
  reseller_admin: [
    'reseller_support',
    'customer_admin',
    'asset_manager',
    'department_head',
    'crew',
  ],
  customer_admin: ['asset_manager', 'department_head', 'crew'],
  asset_manager: ['department_head', 'crew'],
  department_head: [],
  reseller_support: [],
  crew: [],
};

// Roles allowed to create a brand-new yacht (POST /admin/assets). Mirrors the
// backend (assets.js create handler requires customer_admin+).
const CAN_CREATE_YACHT = new Set(['super_admin', 'reseller_admin', 'customer_admin']);

type YachtMode = 'select' | 'create';
type UserMode = 'select' | 'create';

interface NewYachtForm {
  name: string;
  asset_type?: string;
  organizationId?: number;
}

interface AssignmentForm {
  roleOnAsset: string;
  department?: string;
}

// Default sim_type for crew/asset_manager assignments. The user can change it
// on the SIM step before finishing.
const DEFAULT_SIM_TYPE = 'crew';

const YachtSetupWizard: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const callerRole = auth.role || '';

  const allowedChildRoles = useMemo(() => ALLOWED_CHILD_ROLES[callerRole] || [], [callerRole]);
  const canCreateYacht = CAN_CREATE_YACHT.has(callerRole);
  // asset_manager has a single yacht — they can only assign, not create yachts;
  // and step 1 should pre-select that yacht and lock the picker.
  const isAssetManager = callerRole === 'asset_manager';

  // Wizard state
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [yachts, setYachts] = useState<Asset[]>([]);
  const [yachtsLoading, setYachtsLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Step 1
  const [yachtMode, setYachtMode] = useState<YachtMode>('select');
  const [selectedYachtId, setSelectedYachtId] = useState<number | null>(null);
  const [createdYacht, setCreatedYacht] = useState<Asset | null>(null);
  const [yachtCreateForm] = Form.useForm<NewYachtForm>();

  // Step 2
  const [userMode, setUserMode] = useState<UserMode>('select');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [createdUser, setCreatedUser] = useState<{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
  } | null>(null);
  const [userCreateForm] = Form.useForm<UserFormValues>();

  // Step 3
  const [assignForm] = Form.useForm<AssignmentForm>();

  // Step 4 — SIM allocation (optional)
  const [sims, setSims] = useState<UnifiedSim[]>([]);
  const [simsLoading, setSimsLoading] = useState(false);
  const [selectedSimIds, setSelectedSimIds] = useState<string[]>([]);
  const [simSearch, setSimSearch] = useState('');
  const [simType, setSimType] = useState<string>(DEFAULT_SIM_TYPE);

  // Derived selections (carry through the wizard)
  const activeYacht: Asset | null =
    createdYacht || yachts.find(y => y.id === selectedYachtId) || null;
  const activeUserName = createdUser
    ? `${createdUser.first_name} ${createdUser.last_name}`
    : (() => {
        const u = users.find(x => x.id === selectedUserId);
        return u ? `${u.first_name} ${u.last_name}` : '';
      })();
  const activeUserId = createdUser?.id ?? selectedUserId;

  // Load yachts on mount.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setYachtsLoading(true);
      try {
        const [a, o] = await Promise.all([
          getAssets(),
          // Orgs only needed by the yacht-create form, and customer_admin+ can list orgs.
          canCreateYacht ? getOrganizations() : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setYachts(a || []);
        // Yachts must belong to customer orgs (platform/reseller orgs are
        // operator/agency tiers; filter them out of the wizard's selector).
        setOrganizations((o || []).filter(org => org.type === 'customer' && org.is_active));
        // For asset_manager, pre-select the first (and typically only) yacht in
        // their scope and lock the mode to 'select'.
        if (isAssetManager && (a || []).length > 0) {
          setSelectedYachtId(a[0].id);
        }
        // Default yacht-mode: 'select' if any exist, otherwise 'create'.
        setYachtMode((a || []).length === 0 && canCreateYacht ? 'create' : 'select');
      } catch (err: any) {
        message.error(err.message || 'Failed to load yachts');
      } finally {
        if (!cancelled) setYachtsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAssetManager, canCreateYacht]);

  // Load users when we enter step 2.
  useEffect(() => {
    if (step !== 1) return;
    let cancelled = false;
    const load = async () => {
      setUsersLoading(true);
      try {
        const data = await getUsers();
        if (cancelled) return;
        setUsers(data || []);
      } catch (err: any) {
        message.error(err.message || 'Failed to load users');
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [step]);

  // Load SIMs when we enter step 4 (SIM allocation).
  useEffect(() => {
    if (step !== 3) return;
    let cancelled = false;
    const load = async () => {
      setSimsLoading(true);
      try {
        const data = await getSims({ limit: 1000 });
        if (cancelled) return;
        setSims(data || []);
      } catch (err: any) {
        message.error(err.message || 'Failed to load SIMs');
      } finally {
        if (!cancelled) setSimsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [step]);

  // ─── Step 1 submit ─────────────────────────────────────────────────────
  const handleYachtStepNext = async () => {
    if (yachtMode === 'select') {
      if (!selectedYachtId) {
        message.warning('Pick a yacht to continue, or switch to "Create new"');
        return;
      }
      setStep(1);
      return;
    }
    // Create new yacht
    setSubmitting(true);
    try {
      const values = await yachtCreateForm.validateFields();
      if (!values.organizationId && organizations.length === 1) {
        values.organizationId = organizations[0].id;
      }
      const result = await createAsset({
        name: values.name,
        organizationId: values.organizationId!,
        asset_type: values.asset_type || 'yacht',
      });
      // createAsset returns the new row; ensure we have id + org for the next steps
      const newAsset: Asset = {
        id: result.id || result.asset?.id,
        name: result.name || result.asset?.name || values.name,
        organization_id:
          result.organization_id || result.asset?.organization_id || values.organizationId!,
        org_name: result.org_name || '',
        asset_type: result.asset_type || values.asset_type || 'yacht',
        settings: result.settings || null,
        is_active: true,
        created_at: result.created_at || new Date().toISOString(),
        updated_at: result.updated_at || new Date().toISOString(),
      };
      setCreatedYacht(newAsset);
      // Append to the yachts list so the back-and-forward state stays consistent.
      setYachts(prev => [...prev, newAsset]);
      message.success(`Yacht "${newAsset.name}" created`);
      setStep(1);
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error already shown inline
      message.error(err.message || 'Failed to create yacht');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 2 submit ─────────────────────────────────────────────────────
  const handleUserStepNext = async () => {
    if (userMode === 'select') {
      if (!selectedUserId) {
        message.warning('Pick a user to continue, or switch to "Invite new"');
        return;
      }
      setStep(2);
      return;
    }
    // Create new user — fixedOrganizationId is the active yacht's org.
    setSubmitting(true);
    try {
      const values = await userCreateForm.validateFields();
      if (!activeYacht) {
        message.error('No yacht selected — go back to step 1');
        setSubmitting(false);
        return;
      }
      const payload: CreateUserData = {
        email: values.email!,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
        organizationId: activeYacht.organization_id,
        department: values.department || undefined,
        canViewSubordinateSims: values.canViewSubordinateSims,
        canViewYachtSims: values.canViewYachtSims,
      };
      const result = await createUser(payload);
      const newUser = {
        id: (result as any).id,
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email!,
        role: values.role,
      };
      setCreatedUser(newUser);
      message.success(`User ${newUser.first_name} ${newUser.last_name} invited`);
      setStep(2);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 3 next — just validates form and advances to SIM step ───────
  const handleAssignStepNext = async () => {
    if (!activeYacht || !activeUserId) {
      message.error('Missing yacht or user — go back to fix the earlier steps');
      return;
    }
    try {
      await assignForm.validateFields();
      setStep(3);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err.message || 'Please complete the role-on-yacht form');
    }
  };

  // ─── Step 4 finish — commit crew assignment, then SIM allocations ────
  const handleFinish = async () => {
    if (!activeYacht || !activeUserId) {
      message.error('Missing yacht or user — go back to fix the earlier steps');
      return;
    }
    setSubmitting(true);
    try {
      const values = await assignForm.validateFields();
      // 1. Assign user to yacht.
      await ApiClient.post(`/admin/assets/${activeYacht.id}/crew`, {
        userId: activeUserId,
        roleOnAsset: values.roleOnAsset,
        department: values.department || undefined,
      });

      // 2. Assign each selected SIM to the user. Failures are tolerated so
      //    one bad SIM doesn't roll back the whole wizard — surface a warning
      //    summarising what didn't go through.
      const failures: string[] = [];
      for (const simId of selectedSimIds) {
        const sim = sims.find(s => s.identifier === simId);
        if (!sim) continue;
        try {
          await assignSimToUser({
            identifier: sim.identifier,
            provider: sim.provider,
            userId: activeUserId,
            simType: simType || DEFAULT_SIM_TYPE,
          });
        } catch (err: any) {
          failures.push(`${sim.identifier} (${err?.message || 'unknown error'})`);
        }
      }

      if (failures.length > 0) {
        message.warning(
          `Assignment complete, but ${failures.length} SIM(s) failed to assign — assign them manually from the yacht detail page.`
        );
      } else if (selectedSimIds.length > 0) {
        message.success(
          `Assigned ${activeUserName} to ${activeYacht.name} with ${selectedSimIds.length} SIM(s)`
        );
      } else {
        message.success('Assignment complete');
      }
      navigate(`/admin/assets/${activeYacht.id}`);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to complete setup');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render helpers ────────────────────────────────────────────────────
  const renderStep1 = () => {
    if (yachtsLoading) return <Spin />;
    const showCreateToggle = canCreateYacht && !isAssetManager;
    return (
      <div>
        {showCreateToggle && (
          <Radio.Group
            value={yachtMode}
            onChange={e => setYachtMode(e.target.value)}
            style={{ marginBottom: 16 }}
          >
            <Radio.Button value="select" disabled={yachts.length === 0}>
              Use existing
            </Radio.Button>
            <Radio.Button value="create">Create new</Radio.Button>
          </Radio.Group>
        )}

        {yachtMode === 'select' && (
          <Form layout="vertical">
            <Form.Item label="Yacht" required>
              <Select
                value={selectedYachtId ?? undefined}
                onChange={v => setSelectedYachtId(v)}
                placeholder={
                  yachts.length === 0
                    ? 'No yachts in your scope — switch to "Create new"'
                    : 'Select a yacht'
                }
                showSearch
                optionFilterProp="children"
                disabled={isAssetManager || yachts.length === 0}
              >
                {yachts.map(y => (
                  <Select.Option key={y.id} value={y.id}>
                    {y.name} {y.asset_type ? `(${y.asset_type})` : ''}
                    {y.org_name ? ` — ${y.org_name}` : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {isAssetManager && yachts.length > 0 && (
              <Alert
                type="info"
                showIcon
                message="As an asset manager you can only assign users to your own yacht."
              />
            )}
            {yachts.length === 0 && !canCreateYacht && (
              <Empty
                description="No yachts in your scope and you cannot create new yachts."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Form>
        )}

        {yachtMode === 'create' && (
          <Form<NewYachtForm> form={yachtCreateForm} layout="vertical">
            <Form.Item
              name="name"
              label="Yacht name"
              rules={[{ required: true, message: 'Please enter a name' }]}
            >
              <Input placeholder="e.g. M/Y Aurora" />
            </Form.Item>
            <Form.Item name="asset_type" label="Asset type" initialValue="yacht">
              <LookupSelect category="asset_type" placeholder="e.g. yacht, vessel" />
            </Form.Item>
            {organizations.length > 1 && (
              <Form.Item
                name="organizationId"
                label="Customer organization"
                tooltip="Yachts must belong to the customer org that operates them. Platform and reseller orgs are not selectable here."
                rules={[{ required: true, message: 'Pick a customer org' }]}
              >
                <Select
                  placeholder="Select customer organization"
                  showSearch
                  optionFilterProp="children"
                >
                  {organizations.map(o => (
                    <Select.Option key={o.id} value={o.id}>
                      {o.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {organizations.length === 1 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Will be created under "${organizations[0].name}"`}
              />
            )}
            {organizations.length === 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="No customer organizations available"
                description="Yachts can only be created under a customer-type organization. Create one in Organizations first, then return here."
              />
            )}
          </Form>
        )}
      </div>
    );
  };

  const renderStep2 = () => {
    if (!activeYacht) {
      return <Alert type="warning" message="Go back and pick a yacht first." showIcon />;
    }
    const visibleUsers = users.filter(
      u => u.organization_id === activeYacht.organization_id && allowedChildRoles.includes(u.role)
    );
    return (
      <div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Yacht: <Text strong>{activeYacht.name}</Text>{' '}
              {activeYacht.org_name ? `(${activeYacht.org_name})` : ''}
            </span>
          }
        />

        <Radio.Group
          value={userMode}
          onChange={e => setUserMode(e.target.value)}
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="select" disabled={visibleUsers.length === 0}>
            Use existing
          </Radio.Button>
          <Radio.Button value="create">Invite new</Radio.Button>
        </Radio.Group>

        {userMode === 'select' && (
          <Form layout="vertical">
            <Form.Item label="User" required>
              <Select
                value={selectedUserId ?? undefined}
                onChange={v => setSelectedUserId(v)}
                placeholder={
                  usersLoading
                    ? 'Loading…'
                    : visibleUsers.length === 0
                      ? 'No assignable users in this org — switch to "Invite new"'
                      : 'Select a user'
                }
                showSearch
                optionFilterProp="children"
                loading={usersLoading}
                disabled={visibleUsers.length === 0}
              >
                {visibleUsers.map(u => (
                  <Select.Option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} — {u.email}{' '}
                    <Tag style={{ marginLeft: 4 }}>{u.role}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )}

        {userMode === 'create' && (
          <UserForm
            form={userCreateForm}
            mode="create"
            organizations={organizations}
            fixedOrganizationId={activeYacht.organization_id}
          />
        )}
      </div>
    );
  };

  const renderStep3 = () => {
    if (!activeYacht || !activeUserId) {
      return <Alert type="warning" message="Go back and complete the earlier steps." showIcon />;
    }
    return (
      <div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Assigning <Text strong>{activeUserName}</Text> to{' '}
              <Text strong>{activeYacht.name}</Text>
            </span>
          }
        />
        <Form<AssignmentForm> form={assignForm} layout="vertical">
          <Form.Item
            name="roleOnAsset"
            label="Role on yacht"
            rules={[{ required: true, message: 'Pick a role on this yacht' }]}
            initialValue="crew"
          >
            <LookupSelect category="role" placeholder="e.g. captain, engineer, deckhand" />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <LookupSelect category="department" placeholder="e.g. deck, engineering, interior" />
          </Form.Item>
        </Form>
      </div>
    );
  };

  const renderStep4 = () => {
    if (!activeYacht || !activeUserId) {
      return <Alert type="warning" message="Go back and complete the earlier steps." showIcon />;
    }
    // Only show SIMs allocated to the active yacht's org and not yet assigned
    // to a user. Unallocated stock SIMs need to be allocated to the org first
    // (separate flow via SIM inventory).
    let visibleSims = sims.filter(
      s =>
        s.allocation &&
        s.allocation.organizationId === activeYacht.organization_id &&
        !s.allocation.userId
    );
    if (simSearch) {
      const q = simSearch.toLowerCase();
      visibleSims = visibleSims.filter(
        s =>
          s.identifier.toLowerCase().includes(q) ||
          (s.msisdn || '').toLowerCase().includes(q) ||
          (s.alias || '').toLowerCase().includes(q)
      );
    }
    return (
      <div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Allocate SIMs to <Text strong>{activeUserName}</Text> on{' '}
              <Text strong>{activeYacht.name}</Text>. This step is optional — you can skip and
              assign SIMs later from the yacht detail page.
            </span>
          }
        />

        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            placeholder="Search ICCID, MSISDN, alias…"
            value={simSearch}
            onChange={e => setSimSearch(e.target.value)}
            allowClear
            style={{ width: 280 }}
          />
          <Form.Item label="SIM type" style={{ marginBottom: 0 }}>
            <LookupSelect
              category="sim_type"
              value={simType}
              onChange={v => setSimType(v)}
              placeholder="e.g. crew, personal"
            />
          </Form.Item>
        </Space>

        {simsLoading ? (
          <Spin />
        ) : visibleSims.length === 0 ? (
          <Empty
            description="No SIMs allocated to this yacht's org are available for assignment."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            rowKey="identifier"
            size="small"
            pagination={{ pageSize: 10 }}
            dataSource={visibleSims}
            rowSelection={{
              selectedRowKeys: selectedSimIds,
              onChange: keys => setSelectedSimIds(keys as string[]),
            }}
            columns={[
              {
                title: 'Provider',
                dataIndex: 'provider',
                key: 'provider',
                render: (v: string) => <Tag color={v === 'vrs' ? 'blue' : 'green'}>{v}</Tag>,
                width: 100,
              },
              { title: 'Identifier', dataIndex: 'identifier', key: 'identifier' },
              { title: 'MSISDN', dataIndex: 'msisdn', key: 'msisdn' },
              { title: 'Alias', dataIndex: 'alias', key: 'alias' },
              { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
            ]}
          />
        )}
      </div>
    );
  };

  const stepItems = [
    { title: 'Yacht', description: yachtMode === 'create' ? 'Creating new' : 'Selecting existing' },
    { title: 'User', description: userMode === 'create' ? 'Inviting new' : 'Selecting existing' },
    { title: 'Role', description: 'Role + department' },
    {
      title: 'SIMs',
      description: selectedSimIds.length > 0 ? `${selectedSimIds.length} selected` : 'Optional',
    },
  ];

  return (
    <Spin spinning={submitting}>
      <PageHeader
        title="Yacht Setup Wizard"
        actions={
          <PageHelp
            title="Yacht Setup Wizard"
            sections={[
              {
                title: 'What this does',
                content:
                  'Walks you through assigning a user to a yacht in four steps: pick/create the yacht, pick/invite the user, set their role on the yacht, and (optionally) allocate SIMs to them.',
              },
              {
                title: 'Who can use it',
                content:
                  'Super admin, reseller admin, and customer admin can use every step. Asset managers can pick a user and assign — their yacht is pre-selected and locked in step 1.',
              },
              {
                title: 'What gets created',
                content:
                  '- New yacht (if you pick "Create new"): one row in `assets`.\n- New user (if you pick "Invite new"): one row in `users` plus a Cognito invite email.\n- Crew assignment (always): one row in `user_asset_assignments` linking the user to the yacht with a role-on-asset and optional department.\n- SIM assignments (optional): one `resource_assignments` row per SIM you pick, linking it to the user with the chosen sim_type.',
              },
            ]}
          />
        }
      />

      <Steps current={step} items={stepItems} style={{ marginBottom: 24 }} />

      <Card style={{ minHeight: 300, marginBottom: 24 }}>
        {step === 0 && renderStep1()}
        {step === 1 && renderStep2()}
        {step === 2 && renderStep3()}
        {step === 3 && renderStep4()}
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          {step > 0 && (
            <Button onClick={() => setStep(step - 1)} disabled={submitting}>
              Back
            </Button>
          )}
        </div>
        <Space>
          <Button onClick={() => navigate('/admin/orgs-assets')}>Cancel</Button>
          {step < 3 && (
            <Button
              type="primary"
              onClick={
                step === 0
                  ? handleYachtStepNext
                  : step === 1
                    ? handleUserStepNext
                    : handleAssignStepNext
              }
              loading={submitting}
            >
              Next
            </Button>
          )}
          {step === 3 && (
            <Button type="primary" onClick={handleFinish} loading={submitting}>
              {selectedSimIds.length > 0 ? `Finish (${selectedSimIds.length} SIMs)` : 'Finish'}
            </Button>
          )}
        </Space>
      </div>
    </Spin>
  );
};

export default YachtSetupWizard;
